// Phase 9 — mission-lifecycle information architecture.
//
// Single source of truth for the operator console's URL structure. Three
// consumers feed off this table:
//   1. AppShell — renders the top-bar dropdowns from GROUPS + the routes
//      whose `group` matches.
//   2. Breadcrumbs — derives "GROUP > Section > Current" from the route
//      whose `path` matches the browser location (with :id-style params
//      substituted).
//   3. CommandPalette — flattens ROUTES into a "Pages" search category.
//
// Keep this table flat and declarative. Every authenticated page in the
// app should appear here; pages that are deep-link only (e.g. Run Detail
// under /analyse/runs/:id) carry `hiddenFromNav: true` so the top-bar
// dropdowns stay tight, and the breadcrumb + palette still know how to
// label them.

// ---------------------------------------------------------------------
// Mission-lifecycle groups, in left-to-right reading order.
// ---------------------------------------------------------------------
export const GROUPS = [
    {
        id: "plan",
        label: "PLAN",
        description: "Design scenarios and presets.",
    },
    {
        id: "run",
        label: "RUN",
        description: "Execute runs and watch jobs.",
    },
    {
        id: "analyse",
        label: "ANALYSE",
        description: "Inspect results, compare runs, promote goldens.",
    },
    {
        id: "models",
        label: "MODELS & DATA",
        description: "Model registry, calibration, logs, terrain & weather.",
    },
    {
        id: "review",
        label: "REVIEW",
        description: "Reviewer inbox.",
    },
    {
        id: "admin",
        label: "ADMIN",
        description: "Tenants, users, engineering controls.",
        adminOnly: true,
    },
];

// ---------------------------------------------------------------------
// Flat route table. Order within a group = order shown in the dropdown.
//
// Fields:
//   id            — stable identifier (used for React keys and the
//                   `parent` cross-reference for breadcrumbs).
//   path          — react-router path pattern (may include :params).
//   group         — GROUPS[].id this route belongs to.
//   label         — short label for nav + breadcrumbs.
//   description   — one-line helper string shown under the label in the
//                   dropdown panel and in the command palette.
//   pageTitle     — the page-level H1 a PageHeader should render.
//   parent        — (optional) id of another route whose crumb should
//                   appear between the group crumb and this one.
//   hiddenFromNav — (optional) true → omitted from the AppShell dropdown
//                   for this group.
//   hiddenFromPalette — (optional) true → omitted from the CommandPalette
//                   Pages list.
//   keywords      — (optional) extra terms fuzzy-matched by cmdk.
// ---------------------------------------------------------------------
export const ROUTES = [
    // ── PLAN ────────────────────────────────────────────────────────
    {
        id: "plan.scenarios",
        path: "/plan/scenarios",
        group: "plan",
        label: "Scenarios",
        description: "Library of Workbench scenarios — open, clone, or start a new one.",
        pageTitle: "Scenarios",
        keywords: [
            "scenarios", "scenario library", "workbench", "open scenario",
            "new scenario", "clone scenario", "preset scenario",
        ],
    },
    {
        id: "plan.workbench",
        path: "/workbench/:scenarioId",
        group: "plan",
        label: "Workbench",
        description: "Deep-edit a simulation scenario with unit-aware parameters + live validation.",
        pageTitle: "Workbench",
        parent: "plan.scenarios",
        hiddenFromNav: true,
        hiddenFromPalette: true,
        keywords: [
            "simulation workbench", "open in workbench", "scenario editor",
            "three pane", "validation gate", "units",
        ],
    },
    {
        id: "plan.airframes",
        path: "/plan/airframes",
        group: "plan",
        label: "Airframes",
        description: "Library of platforms — presets + your own — feeding the Workbench.",
        pageTitle: "Airframes",
        keywords: [
            "airframe library", "jsbsim", "aero", "thrust", "mass", "inertia",
            "platform", "interceptor", "threat", "preset",
        ],
    },
    {
        id: "plan.environments",
        path: "/plan/environments",
        group: "plan",
        label: "Environments",
        description: "Atmosphere, wind, turbulence, gusts, visibility and solar — the reproducible weather layer.",
        pageTitle: "Environments",
        keywords: [
            "environment", "atmosphere", "isa", "wind", "log-law", "power-law",
            "turbulence", "dryden", "von karman", "gust", "visibility", "solar",
            "weather",
        ],
    },
    {
        id: "plan.terrain",
        path: "/plan/terrain",
        group: "plan",
        label: "Terrain",
        description: "DEM tiles — upload GeoTIFFs or fetch public SRTM via OpenTopography.",
        pageTitle: "Terrain",
        keywords: [
            "terrain", "dem", "srtm", "elevation", "hillshade", "geotiff",
            "opentopography", "tile picker", "bbox",
        ],
    },
    {
        id: "plan.obstacles",
        path: "/plan/obstacles",
        group: "plan",
        label: "Obstacles",
        description: "GeoJSON obstacle packs — polygons with per-feature height_m for RF line-of-sight.",
        pageTitle: "Obstacles",
        keywords: [
            "obstacles", "geojson", "polygon", "buildings", "rf", "line of sight",
            "los", "height", "urban canyon",
        ],
    },
    {
        id: "plan.threats",
        path: "/plan/threats",
        group: "plan",
        label: "Threats",
        description: "Threat packages — waypoint ingress, swarms, evasive manoeuvres, comms-denied fallbacks.",
        pageTitle: "Threats",
        keywords: [
            "threats", "waypoint", "swarm", "wedge", "line", "diamond", "column",
            "evasive", "weave", "jink", "terrain-hug", "comms-denied", "fallback",
            "loitering munition",
        ],
    },
    {
        id: "plan.guidance-profiles",
        path: "/plan/guidance-profiles",
        group: "plan",
        label: "Guidance",
        description: "Interceptor guidance + seeker + lock-loss profiles.",
        pageTitle: "Guidance profiles",
        keywords: [
            "guidance", "interceptor", "pure pursuit", "pn", "apn", "clos",
            "seeker", "rf", "eo ir", "fov", "snr", "lock loss", "reacquire",
        ],
    },
    {
        id: "plan.engagements",
        path: "/plan/engagements",
        group: "plan",
        label: "Engagements",
        description: "Warhead + fuzing + Pk curve + salvo doctrine.",
        pageTitle: "Engagements",
        keywords: [
            "engagement", "lethality", "warhead", "blast", "fragmentation",
            "kinetic", "net capture", "directed energy", "fuze", "proximity",
            "contact", "pk curve", "salvo", "shoot-look-shoot",
        ],
    },

    // ── RUN ─────────────────────────────────────────────────────────
    // Pass 3 — Quick Sim is the first entry in RUN so the non-expert
    // path is visually adjacent to PLAN > Scenarios in the header nav.
    {
        id: "run.quick_sim",
        path: "/quick-sim",
        group: "run",
        label: "Quick Sim",
        description:
            "Pick a target, an interceptor, a scenario flavour. One click to Run.",
        pageTitle: "Quick Sim",
        keywords: [
            "quick sim", "demo", "try it", "one click", "non-expert",
            "first run", "flagship", "see a kill",
        ],
    },
    {
        id: "run.matrix",
        path: "/run/matrix",
        group: "run",
        label: "Matrix Run",
        description:
            "N × M (× K) batch kickoff for environment / threat / guidance sweeps.",
        pageTitle: "Matrix Run",
        keywords: [
            "matrix", "sweep", "batch", "fan out", "n by m",
            "environment matrix", "analyst", "compare",
        ],
    },
    {
        id: "run.matrix_results",
        path: "/run/matrix/:matrixJobId",
        group: "run",
        label: "Matrix Results",
        description: "Live grid of cell-level outcomes for a matrix job.",
        pageTitle: "Matrix Results",
        parent: "run.matrix",
        hiddenFromNav: true,
        hiddenFromPalette: true,
    },
    {
        id: "run.optimisation",
        path: "/run/optimisation",
        group: "run",
        label: "Design Optimisation",
        description:
            "Search the design space for a configuration that hits a Pk target at minimum design cost.",
        pageTitle: "Design Optimisation",
        keywords: [
            "optimisation", "optimization", "pk", "design optimisation",
            "reverse optimisation", "roe", "recommend", "recommended configuration",
            "study", "design study", "design space search",
        ],
    },
    {
        id: "run.optimisation_new",
        path: "/run/optimisation/new",
        group: "run",
        label: "New Optimisation Study",
        description: "Compose a new design optimisation study.",
        pageTitle: "New Optimisation Study",
        parent: "run.optimisation",
        hiddenFromNav: true,
        keywords: [
            "new optimisation", "compose study", "new design study",
            "start optimisation",
        ],
    },
    {
        id: "run.optimisation_detail",
        path: "/run/optimisation/:studyId",
        group: "run",
        label: "Optimisation Study",
        description: "Live progress and recommended configuration for a design optimisation study.",
        pageTitle: "Optimisation Study",
        parent: "run.optimisation",
        hiddenFromNav: true,
        hiddenFromPalette: true,
    },
    // Phase 14.5 — Unified Campaign Builder UX.
    //
    // The unified `/campaigns` route is the new operator entry point;
    // it consolidates the Phase 10 / 11 / 12 / 13 / 14 campaign
    // creation flows behind a single guided wizard. Phase 14.6
    // (mini-phase, 2026-05-08) retired the legacy `(legacy)`-tagged
    // sidebar entries (`run.pk_campaigns`, `run.geometry_campaigns`)
    // — the unified surface is now the only sidebar-visible campaign
    // entry. Canonical detail-route metadata (`run.pk_campaign_detail`,
    // `run.geometry_campaign_detail`) is preserved as `hiddenFromNav`
    // because the unified `/campaigns/:id` dispatcher navigates to
    // those URLs.
    {
        id: "run.campaigns",
        path: "/campaigns",
        group: "run",
        label: "Campaigns",
        description:
            "Unified campaign builder: pick a scenario, toggle perturbation / sensitivity / target-Pk / geometry / degradation, dispatch to the right backend.",
        pageTitle: "Campaigns",
        keywords: [
            "campaigns", "unified campaigns", "campaign builder",
            "new campaign", "perturbation", "sensitivity", "target pk",
            "target-pk", "geometry sweep", "degradation",
        ],
    },
    {
        id: "run.campaigns_new",
        path: "/campaigns/new",
        group: "run",
        label: "New campaign",
        description: "Guided builder for a new campaign — single-page wizard.",
        pageTitle: "New campaign",
        parent: "run.campaigns",
        hiddenFromNav: true,
        hiddenFromPalette: true,
    },
    {
        id: "run.campaign_detail",
        path: "/campaigns/:campaignId",
        group: "run",
        label: "Campaign",
        description:
            "Campaign detail (dispatches to the canonical /pk-campaigns or /geometry-campaigns detail surface).",
        pageTitle: "Campaign",
        parent: "run.campaigns",
        hiddenFromNav: true,
        hiddenFromPalette: true,
    },
    {
        id: "run.degradation_profiles",
        path: "/degradation-profiles",
        group: "run",
        label: "Degradation Profiles",
        description:
            "Library of post-engine degradation profiles (IMU / radar bias + drift, Markov track quality, data-starvation, modulation thresholds). Attached to a Geometry campaign at submission.",
        pageTitle: "Degradation Profiles",
        keywords: [
            "degradation", "degradation profile", "imu drift", "radar bias",
            "markov", "track quality", "data starvation", "latency",
            "modulation thresholds", "stress reference", "friendly default",
        ],
    },
    {
        id: "run.degradation_profile_new",
        path: "/degradation-profiles/new",
        group: "run",
        label: "New profile",
        description: "Composer for a new degradation profile.",
        pageTitle: "New degradation profile",
        parent: "run.degradation_profiles",
        hiddenFromNav: true,
        hiddenFromPalette: true,
    },
    {
        id: "run.degradation_profile_detail",
        path: "/degradation-profiles/:profileId",
        group: "run",
        label: "Profile",
        description: "Edit a degradation profile.",
        pageTitle: "Degradation profile",
        parent: "run.degradation_profiles",
        hiddenFromNav: true,
        hiddenFromPalette: true,
    },
    // Phase 10 — Pk Effectiveness Engine v1.
    // Phase 14.6 retired the bare `/pk-campaigns` list entry; only
    // the canonical `:campaignId` detail metadata remains (used by
    // the unified `/campaigns/:id` dispatcher to populate page
    // titles + breadcrumbs).
    {
        id: "run.pk_campaign_detail",
        path: "/pk-campaigns/:campaignId",
        group: "run",
        label: "Pk Campaign",
        description:
            "Detail view of one Pk effectiveness campaign — KPIs, failure modes, distributions, trajectories, report.",
        pageTitle: "Pk Campaign",
        parent: "run.campaigns",
        hiddenFromNav: true,
        hiddenFromPalette: true,
    },
    // Phase 13 — Geometry Randomisation Engine.
    // Phase 14.6 retired the bare `/geometry-campaigns` list entry
    // (same disposition as Pk above). Only the canonical detail
    // metadata remains.
    {
        id: "run.geometry_campaign_detail",
        path: "/geometry-campaigns/:campaignId",
        group: "run",
        label: "Geometry Campaign",
        description:
            "Detail view of one geometry_sweep campaign — Slice Pk distribution, trajectory groups, per-slice table.",
        pageTitle: "Geometry Campaign",
        parent: "run.campaigns",
        hiddenFromNav: true,
        hiddenFromPalette: true,
    },
    {
        id: "run.history",
        path: "/run/history",
        group: "run",
        label: "Run History",
        description: "All runs in this tenant. Start new runs from the Scenarios library.",
        pageTitle: "Run History",
        keywords: ["runs", "list runs", "past runs", "new run", "start run"],
    },
    {
        id: "run.jobs",
        path: "/run/jobs",
        group: "run",
        label: "Jobs",
        description: "Long-running jobs (LHS, sensitivity, optimisation).",
        pageTitle: "Jobs",
        keywords: ["lhs", "sensitivity", "optimisation"],
    },

    // ── ANALYSE ─────────────────────────────────────────────────────
    {
        id: "analyse.detail",
        path: "/analyse/runs/:id",
        group: "analyse",
        label: "Run Detail",
        description: "Deep-link a single run.",
        pageTitle: "Run Detail",
        parent: "run.history",
        hiddenFromNav: true,
        hiddenFromPalette: true,
    },
    {
        id: "analyse.compare",
        path: "/analyse/compare",
        group: "analyse",
        label: "Compare Runs",
        description: "Side-by-side comparison of two or more runs.",
        pageTitle: "Compare Runs",
        keywords: ["diff runs", "regression"],
    },
    {
        id: "analyse.golden",
        path: "/analyse/golden-runs",
        group: "analyse",
        label: "Golden Runs",
        description: "Blessed replay baselines.",
        pageTitle: "Golden Runs",
        keywords: ["promote", "baseline", "replay"],
    },

    // ── MODELS & DATA ───────────────────────────────────────────────
    {
        id: "models.registry",
        path: "/models/registry",
        group: "models",
        label: "Model Registry",
        description: "Aero + sensor models catalogue.",
        pageTitle: "Model Registry",
        keywords: ["aircraft", "sensors", "model versions"],
    },
    {
        id: "models.calibration",
        path: "/models/calibration",
        group: "models",
        label: "Calibration Sessions",
        description: "Propose, accept or reject parameter deltas.",
        pageTitle: "Calibration Sessions",
        keywords: ["calibrate", "tune", "parameter deltas"],
    },
    {
        id: "models.logs",
        path: "/models/logs",
        group: "models",
        label: "Imported Logs",
        description: "PX4 ULog / ArduPilot bin / MAVLink tlog / CSV telemetry.",
        pageTitle: "Imported Logs",
        keywords: ["telemetry", "px4", "ardupilot", "mavlink", "csv"],
    },

    // ── REVIEW ──────────────────────────────────────────────────────
    {
        id: "review.inbox",
        path: "/review/inbox",
        group: "review",
        label: "Reviewer Inbox",
        description: "Runs awaiting reviewer label decisions.",
        pageTitle: "Reviewer Inbox",
        keywords: ["approve", "reject", "label"],
    },

    // ── ADMIN (hidden unless role ∈ {admin, superadmin}) ────────────
    {
        id: "admin.home",
        path: "/admin",
        group: "admin",
        label: "Admin Console",
        description: "Operator-only landing.",
        pageTitle: "Admin Console",
        keywords: ["admin home"],
    },
    {
        id: "admin.tenants",
        path: "/admin/tenants",
        group: "admin",
        label: "Tenants",
        description: "Tenant directory + subscription grants.",
        pageTitle: "Tenants",
        parent: "admin.home",
    },
    {
        id: "admin.users",
        path: "/admin/users",
        group: "admin",
        label: "Users",
        description: "User directory + role assignment.",
        pageTitle: "Users",
        parent: "admin.home",
    },
    {
        id: "admin.engineering",
        path: "/admin/engineering",
        group: "admin",
        label: "Engineering Settings",
        description: "Backend self-check, runtime flags, queue depth.",
        pageTitle: "Engineering Settings",
        parent: "admin.home",
        keywords: ["selfcheck", "backend"],
    },
    {
        id: "admin.training",
        path: "/admin/training-data",
        group: "admin",
        label: "Training Data",
        description: "Mirror export window.",
        pageTitle: "Training Data",
        parent: "admin.home",
    },
    {
        id: "admin.raw",
        path: "/admin/raw-presets",
        group: "admin",
        label: "Raw YAML Access",
        description: "System preset YAML inspector (admin only).",
        pageTitle: "Raw Preset Access",
        parent: "admin.home",
        keywords: ["preset yaml", "raw preset"],
    },
];

// Routes that predate the IA restructure but are still reachable for
// back-compat / link stability. Hidden from nav + palette but present
// so Breadcrumbs can still label them.
export const LEGACY_ROUTES = [
    { id: "legacy.parameter_sets", path: "/parameter-sets", pageTitle: "Parameter Sets", group: null },
    { id: "legacy.parameter_set_detail", path: "/parameter-sets/:id", pageTitle: "Parameter Set", group: null },
    { id: "legacy.help", path: "/help", pageTitle: "User guide", group: null, keywords: ["help", "user guide", "documentation", "shortcuts", "getting started", "workbench", "glossary"] },
    { id: "legacy.change_password", path: "/account/change-password", pageTitle: "Change Password", group: null },
    { id: "legacy.forbidden", path: "/403", pageTitle: "Forbidden", group: null },
];

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

/** Returns the ROUTES + LEGACY_ROUTES that belong to a nav group. */
export function routesForGroup(groupId) {
    return ROUTES.filter((r) => r.group === groupId && !r.hiddenFromNav);
}

/** Returns GROUPS filtered to those the caller's role can see. */
export function visibleGroups({ isAdmin } = {}) {
    return GROUPS.filter((g) => !g.adminOnly || isAdmin);
}

/** Find the route whose path pattern matches the current pathname. */
export function matchRoute(pathname) {
    const all = [...ROUTES, ...LEGACY_ROUTES];
    // Exact match first.
    const exact = all.find((r) => r.path === pathname);
    if (exact) return exact;
    // Pattern match (e.g. "/analyse/runs/:id" against "/analyse/runs/abc").
    const segs = pathname.split("/").filter(Boolean);
    const candidates = all.filter((r) => {
        const pattern = r.path.split("/").filter(Boolean);
        if (pattern.length !== segs.length) return false;
        return pattern.every((p, i) => p.startsWith(":") || p === segs[i]);
    });
    // Prefer the most specific (fewest param segments).
    candidates.sort((a, b) => {
        const ap = a.path.split("/").filter((s) => s.startsWith(":")).length;
        const bp = b.path.split("/").filter((s) => s.startsWith(":")).length;
        return ap - bp;
    });
    return candidates[0] || null;
}

/** Substitute :params in a path pattern with actual values. */
export function fillPath(pattern, params = {}) {
    return pattern
        .split("/")
        .map((seg) => {
            if (seg.startsWith(":")) {
                const key = seg.slice(1);
                return params[key] ?? seg;
            }
            return seg;
        })
        .join("/");
}

/** Lookup a GROUPS row by id. */
export function groupById(groupId) {
    return GROUPS.find((g) => g.id === groupId) || null;
}

/** Lookup a ROUTES row by id. */
export function routeById(routeId) {
    return [...ROUTES, ...LEGACY_ROUTES].find((r) => r.id === routeId) || null;
}
